import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { ChatAttachment, ChatContextType, DBChatMessage } from '../lib/supabase';
import { useAuthStore, getActedByName } from './authStore';
import { markOwnWrite } from '../lib/realtime/ownWrites';

export interface ChatMessage {
  id: string;
  contextType: ChatContextType;
  contextId: string;
  authorId: string | null;
  authorName: string;
  text: string;
  attachments: ChatAttachment[];
  createdAt: Date;
}

const STORAGE_BUCKET = 'chat-attachments';

export function chatContextKey(contextType: ChatContextType, contextId: string): string {
  return `${contextType}:${contextId}`;
}

function toMessage(row: DBChatMessage): ChatMessage {
  return {
    id: row.id,
    contextType: row.context_type,
    contextId: row.context_id,
    authorId: row.author_id,
    authorName: row.author_name,
    text: row.text,
    attachments: row.attachments ?? [],
    createdAt: new Date(row.created_at),
  };
}

async function uploadAttachment(contextType: ChatContextType, contextId: string, file: File): Promise<ChatAttachment> {
  const uuid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  const safeName = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
  const path = `${contextType}/${contextId}/${uuid}-${safeName}`;

  const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, {
    contentType: file.type || undefined,
    upsert: false,
  });
  if (error) throw error;

  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return {
    name: file.name,
    path,
    url: data.publicUrl,
    size: file.size,
    mimeType: file.type || 'application/octet-stream',
  };
}

interface ChatState {
  messagesByContext: Record<string, ChatMessage[]>;
  loadingByContext: Record<string, boolean>;
  sendingByContext: Record<string, boolean>;
  errorByContext: Record<string, string | null>;

  loadMessages: (contextType: ChatContextType, contextId: string) => Promise<void>;
  sendMessage: (
    contextType: ChatContextType,
    contextId: string,
    text: string,
    files?: File[],
  ) => Promise<boolean>;
  clearError: (contextType: ChatContextType, contextId: string) => void;
  applyRemoteRow: (event: 'INSERT' | 'UPDATE' | 'DELETE', row: Record<string, unknown> | null) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  messagesByContext: {},
  loadingByContext: {},
  sendingByContext: {},
  errorByContext: {},

  loadMessages: async (contextType, contextId) => {
    const key = chatContextKey(contextType, contextId);
    set(s => ({
      loadingByContext: { ...s.loadingByContext, [key]: true },
      errorByContext: { ...s.errorByContext, [key]: null },
    }));

    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('context_type', contextType)
      .eq('context_id', contextId)
      .order('created_at', { ascending: true });

    if (error) {
      set(s => ({
        loadingByContext: { ...s.loadingByContext, [key]: false },
        errorByContext: { ...s.errorByContext, [key]: error.message },
      }));
      return;
    }

    const messages = (data ?? []).map(row => toMessage(row as DBChatMessage));
    set(s => ({
      messagesByContext: { ...s.messagesByContext, [key]: messages },
      loadingByContext: { ...s.loadingByContext, [key]: false },
    }));
  },

  sendMessage: async (contextType, contextId, text, files = []) => {
    const key = chatContextKey(contextType, contextId);
    const user = useAuthStore.getState().user;
    if (!user) return false;

    const trimmed = text.trim();
    if (!trimmed && files.length === 0) return false;

    set(s => ({ sendingByContext: { ...s.sendingByContext, [key]: true } }));

    let attachments: ChatAttachment[] = [];
    try {
      if (files.length > 0) {
        attachments = await Promise.all(files.map(file => uploadAttachment(contextType, contextId, file)));
      }
    } catch (err) {
      set(s => ({
        sendingByContext: { ...s.sendingByContext, [key]: false },
        errorByContext: { ...s.errorByContext, [key]: err instanceof Error ? err.message : 'Failed to upload attachment' },
      }));
      return false;
    }

    const authorName = getActedByName() ?? 'Unknown';
    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        context_type: contextType,
        context_id: contextId,
        author_id: user.id,
        author_name: authorName,
        text: trimmed,
        attachments,
      })
      .select()
      .single();

    if (error || !data) {
      set(s => ({
        sendingByContext: { ...s.sendingByContext, [key]: false },
        errorByContext: { ...s.errorByContext, [key]: error?.message ?? 'Failed to send message' },
      }));
      return false;
    }

    const message = toMessage(data as DBChatMessage);
    markOwnWrite('chat_messages', message.id);
    set(s => ({
      messagesByContext: { ...s.messagesByContext, [key]: [...(s.messagesByContext[key] ?? []), message] },
      sendingByContext: { ...s.sendingByContext, [key]: false },
      errorByContext: { ...s.errorByContext, [key]: null },
    }));
    return true;
  },

  clearError: (contextType, contextId) => {
    const key = chatContextKey(contextType, contextId);
    set(s => ({ errorByContext: { ...s.errorByContext, [key]: null } }));
  },

  applyRemoteRow: (event, row) => {
    const id = row?.id as string | undefined;
    if (!id) return;
    if (event === 'DELETE') {
      set((s) => {
        const next = { ...s.messagesByContext };
        for (const key of Object.keys(next)) {
          next[key] = next[key].filter((m) => m.id !== id);
        }
        return { messagesByContext: next };
      });
      return;
    }
    if (!row?.context_type || !row?.context_id) return;
    const message = toMessage(row as unknown as DBChatMessage);
    const key = chatContextKey(message.contextType, message.contextId);
    set((s) => {
      const loaded = s.messagesByContext[key];
      if (!loaded) return s;
      if (loaded.some((m) => m.id === message.id)) {
        return {
          messagesByContext: {
            ...s.messagesByContext,
            [key]: loaded.map((m) => (m.id === message.id ? message : m)),
          },
        };
      }
      return {
        messagesByContext: {
          ...s.messagesByContext,
          [key]: [...loaded, message],
        },
      };
    });
  },
}));
